import { useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { actions, selectors } from 'darwin_core';

import BackLink from '../navigation/BackLink.jsx';
import { homePath } from '../navigation/paths.js';
import { useHomeRouteContext } from '../navigation/HomeRoute.jsx';
import { useWebSocket } from '../transport/WebSocketProvider.jsx';
import {
  holidayPickerBounds,
  isValidHolidayEnd,
} from '../controls/holidayModel.js';
import {
  homeHolidayMessage,
  homeHolidayStatus,
  sameHomeHolidayStatus,
} from '../controls/holidayStatus.js';
import { useControlsSendable } from '../controls/useControlsSendable.js';
import useScreen from '../hooks/useScreen.js';

// Whole-home Holiday Mode — the dedicated action screen, reached from
// the Property action row. Unlike the per-zone controls, holiday spans the whole
// home: darwin_core's startHolidayAllZones takes every CH zone's [prtId, endpointId]
// tuple and one end time, and cancelHolidayAllZones ends it for the home — so this
// reads the home's CH zones, not a single zone.
//
// The screen mirrors the app's HolidayMode flow (Property/HolidayMode.js): while
// away it offers to cancel; otherwise it takes a return date/time and starts the
// holiday. Applying either dispatches the optimistic command and returns to the
// Property screen — the same dispatch-and-return model the other dedicated action
// screens use (Plus Hours, Heating Status). Holiday is the one control that fails
// loudly; a rejection or timeout surfaces on the Property screen's
// ControlErrorBanner, which is where returning lands.

const noop = () => {};

export default function HolidayScreen() {
  const headingRef = useScreen('Holiday Mode');
  const { homeId } = useHomeRouteContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ws = useWebSocket();
  const sendWsMessage = ws?.sendWsMessage ?? noop;
  // A Start/Cancel dispatched while the socket is down is silently dropped, so gate
  // both on an Open connection (holiday is the one control meant to fail loudly).
  const { sendable } = useControlsSendable();

  const homeName = useSelector((state) =>
    selectors.getHomeFriendlyName(state, homeId),
  );

  // CH zones carry the holiday state and are the tuples startHolidayAllZones
  // expects. Drop any malformed zone (a known type that arrived with no zoneId —
  // the same untrusted-data guard the read model applies) so a per-zone selector
  // is never handed an undefined tuple. shallowEqual keeps the ref stable
  // (getChZones rebuilds the array but reuses the zone objects).
  const chZones = useSelector(
    (state) =>
      selectors
        .getChZones(state, homeId)
        .filter((zone) => Array.isArray(zone.zoneId)),
    shallowEqual,
  );

  // Which heating zones are away (reported or optimistic — a change made here
  // flips it before the device acks), read per thermostat. Not
  // getNearestHolidayEndTime: a device can report holiday with NO end time, and
  // reading that as "not away" would offer Start over a holiday that is running
  // (the Holiday notice elsewhere links here to cancel it). And in a two-zone home
  // only one thermostat may be away — the wording below names it, matching the
  // notice, while Cancel still ends the holiday for every thermostat that is away.
  const holiday = useSelector(
    (state) => homeHolidayStatus(state, homeId),
    sameHomeHolidayStatus,
  );
  const inHoliday = holiday.away.length > 0;

  // Shared Desired State: another client (a second browser, a phone, a voice
  // assistant) has an accepted leave-holiday request in flight for this home.
  // Our own cancel would then duplicate it — and a duplicate cancel that reaches
  // a zone which has already left holiday is rejected by the firmware, which
  // crash-resyncs the device. So the cancel stands down and says why (matching
  // the app), rather than greying out mutely. getSharedHolidayExitPending stands
  // down for our OWN pending cancel, so this only fires for a peer's. NB it is
  // inert until the backend broadcasts desiredState to the web socket (the slice
  // never fills otherwise) — the behaviour is proven here against seeded state.
  const peerCancelInFlight = useSelector((state) =>
    chZones.some((zone) =>
      Boolean(
        selectors.getSharedHolidayExitPending(state, homeId, zone.zoneId),
      ),
    ),
  );
  const cancelInFlight = inHoliday && peerCancelInFlight;

  // Bounds mirror the validator: earliest tomorrow, latest 90 days out, default a
  // week away. Frozen at mount so the picker does not jump as the clock ticks.
  const { minValue, maxValue, defaultValue } = useMemo(
    () => holidayPickerBounds(Date.now()),
    [],
  );
  const [endValue, setEndValue] = useState(defaultValue);
  const endValid = isValidHolidayEnd(endValue);

  const start = () => {
    if (!endValid || chZones.length === 0) {
      return;
    }
    dispatch(
      actions.startHolidayAllZones(
        sendWsMessage,
        homeId,
        chZones.map((zone) => zone.zoneId),
        new Date(endValue),
      ),
    );
    navigate(homePath(homeId));
  };

  const cancel = () => {
    if (cancelInFlight) {
      return;
    }
    dispatch(actions.cancelHolidayAllZones(sendWsMessage, homeId));
    navigate(homePath(homeId));
  };

  let body;
  if (chZones.length === 0) {
    body = (
      <p className="dashboard-status" role="status">
        Holiday Mode isn’t available for this property.
      </p>
    );
  } else if (inHoliday) {
    body = (
      <section className="holiday-screen__panel">
        <p className="holiday-screen__status" aria-live="polite">
          {homeHolidayMessage(holiday)}
        </p>
        <p className="holiday-screen__hint">
          While Holiday Mode is active you can’t set another holiday.
        </p>
        {!sendable && (
          <p className="holiday-screen__hint">
            You appear to be offline — reconnect to cancel Holiday Mode.
          </p>
        )}
        <button
          type="button"
          className="holiday-screen__action"
          disabled={cancelInFlight || !sendable}
          onClick={cancel}
        >
          {cancelInFlight ? 'Cancelling Holiday…' : 'Cancel Holiday'}
        </button>
      </section>
    );
  } else {
    body = (
      <section className="holiday-screen__panel">
        <p id="holiday-hint" className="holiday-screen__hint">
          Heating will be off during this time, but Frost Protection stays
          active. Choose when your holiday should finish.
        </p>
        <label className="holiday-screen__field" htmlFor="holiday-end">
          Return date &amp; time
        </label>
        <input
          id="holiday-end"
          type="datetime-local"
          className="holiday-screen__input"
          value={endValue}
          min={minValue}
          max={maxValue}
          aria-describedby="holiday-hint"
          onChange={(event) => setEndValue(event.target.value)}
        />
        {!sendable && (
          <p className="holiday-screen__hint">
            You appear to be offline — reconnect to start Holiday Mode.
          </p>
        )}
        <button
          type="button"
          className="holiday-screen__action"
          disabled={!endValid || !sendable}
          onClick={start}
        >
          Start Holiday
        </button>
      </section>
    );
  }

  return (
    <main className="app-shell screen holiday-screen">
      <header className="screen__header">
        <BackLink to={homePath(homeId)} label="Property" />
        <h1 id="main-content" tabIndex={-1} ref={headingRef}>
          Holiday Mode
        </h1>
        {homeName ? <p className="screen__sub">{homeName}</p> : null}
      </header>
      {body}
    </main>
  );
}
